import {
  GanttFeatureList,
  GanttFeatureListGroup,
  GanttFeatureRow,
  GanttProvider,
  GanttSidebar,
  GanttSidebarGroup,
  GanttSidebarItem,
  GanttTimeline,
  type GanttFeature,
} from '@/components/kibo-ui/gantt'
import { ABSENCE_REQUESTS_GANTT_QUERY } from '@/modules/timesheets/queries'
import type { ResultNode } from '@/types/util'
import { format, useUser } from '@/utils'
import { getNodes } from '@/utils/graphql'
import type { UseLeaderGroupsSuspenseReturn } from '@/utils/hooks/useLeaderGroups'
import { useSuspenseQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  getISOWeek,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subSeconds,
} from 'date-fns'
import type { VariablesOf } from 'gql.tada'
import { useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import { type AbsenceFilterForm } from '../types'
import theme from '@/theme'
import { AbsenceDialog, type AbsenceDialogRef } from './absence-dialog'

const MIN_ZOOM = 1
const MAX_ZOOM = 500
const DEFAULT_ZOOM = 10
const WEEKLY_HEADER_THRESHOLD = 24
const GANTT_BASE_COLUMN_WIDTH = 5000
const GANTT_COLUMN_SCALE_DIVISOR = 2400
const GANTT_DESKTOP_HEADER_HEIGHT = 80
const GANTT_DESKTOP_ROW_HEIGHT = 36
const GANTT_GROUP_GAP = 16
const ABSENCE_STATUS_COLORS = {
  APPROVED: theme.colors.green,
  PENDING: theme.colors.orange,
  REJECTED: theme.colors.red,
} as const

type AbsenceRequestNode = ResultNode<
  typeof ABSENCE_REQUESTS_GANTT_QUERY,
  'pendingAbsences'
>

type UserRow = {
  userId: string
  userName: string
  leaderGroupId: string
  leaderGroupName: string
  features: GanttFeature[]
  sidebarUsage: string | null
}

interface Props {
  filter: AbsenceFilterForm
  leaderGroups: UseLeaderGroupsSuspenseReturn
  canEditForOthers: boolean
  createLockedBefore: Date | null
}

export function AbsenceRequestsGantt({
  filter,
  leaderGroups,
  canEditForOthers,
  createLockedBefore,
}: Props) {
  const navigate = useNavigate()
  const translations = useTranslate({
    noData: 'absences.requests.gantt.no-data',
    noResults: 'common.no-results',
    none: 'common.none',
    open: 'common.open',
    editAbsence: 'timesheets.edit-absence',
  })
  const dialogRef = useRef<AbsenceDialogRef>(null)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const me = useUser()

  const users = filter.users
  const absenceType = filter.absenceType
  const leaderGroup = filter.leaderGroup
  const byCreatedDate = filter.byCreatedDate
  const { from, to } = filter.dateRange

  const queryVariables: VariablesOf<typeof ABSENCE_REQUESTS_GANTT_QUERY> = {
    first: 500,
    absenceType: absenceType || undefined,
    includeSuperuser: me.isSuperuser,
    users,
    leaderGroup,
  }

  if (byCreatedDate) {
    queryVariables.createdAtGte = from?.toISOString()
    queryVariables.createdAtLte = to?.toISOString()
  } else {
    queryVariables.datetimeEndGte = from?.toISOString()
    queryVariables.datetimeStartLte = to?.toISOString()
  }

  const { data } = useSuspenseQuery(ABSENCE_REQUESTS_GANTT_QUERY, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  const absences = useMemo(
    () => [
      ...getNodes(data?.approvedAbsences),
      ...getNodes(data?.pendingAbsences),
    ],
    [data?.approvedAbsences, data?.pendingAbsences]
  )
  const absencesById = useMemo(
    () => new Map(absences.map(absence => [absence.id, absence])),
    [absences]
  )
  const hideSidebarSums = useMemo(() => {
    if (!absenceType) return true

    const absenceTypeIds = new Set(
      absences.map(absence => absence.absenceType?.id).filter(Boolean)
    )

    return absenceTypeIds.size > 1
  }, [absenceType, absences])

  const groupedRows = useMemo(() => {
    const selectedUserIds = users.length > 0 ? new Set(users) : null
    // allUsersList excludes deactivated users (includeInactive: false), so use
    // it as the allowlist of visible users. Leader-group membership and absence
    // records aren't filtered by active status, so without this gate a
    // deactivated user could still get a row via those two sources.
    const activeUserIds = new Set(
      (data?.allUsersList ?? []).map(user => user.id)
    )
    const visibleLeaderGroups = leaderGroups.myLeaderGroups.filter(
      group => !leaderGroup || group.id === leaderGroup
    )
    const leaderGroupsByUserId = new Map<
      string,
      UseLeaderGroupsSuspenseReturn['myLeaderGroups'][number]
    >()
    const rows = new Map<string, UserRow>()

    visibleLeaderGroups.forEach(group => {
      group.users.forEach(user => {
        if (selectedUserIds && !selectedUserIds.has(user.id)) return
        if (!activeUserIds.has(user.id)) return

        if (!leaderGroupsByUserId.has(user.id)) {
          leaderGroupsByUserId.set(user.id, group)
        }

        if (!rows.has(user.id)) {
          rows.set(user.id, {
            userId: user.id,
            userName: user.fullName,
            leaderGroupId: group.id,
            leaderGroupName: group.name,
            features: [],
            sidebarUsage: null,
          })
        }
      })
    })

    if (!leaderGroup) {
      data?.allUsersList.forEach(user => {
        if (selectedUserIds && !selectedUserIds.has(user.id)) return
        if (rows.has(user.id)) return

        rows.set(user.id, {
          userId: user.id,
          userName: user.fullName,
          leaderGroupId: '',
          leaderGroupName: translations.none,
          features: [],
          sidebarUsage: null,
        })
      })
    }

    absences.forEach(absence => {
      if (selectedUserIds && !selectedUserIds.has(absence.user.id)) return
      if (!activeUserIds.has(absence.user.id)) return

      const userGroup = leaderGroupsByUserId.get(absence.user.id)
      const existingRow = rows.get(absence.user.id)
      const row: UserRow = existingRow ?? {
        userId: absence.user.id,
        userName: absence.user.fullName,
        leaderGroupId: userGroup?.id ?? '',
        leaderGroupName: userGroup?.name ?? translations.none,
        features: [],
        sidebarUsage: null,
      }

      row.features.push(createFeature(absence))
      row.sidebarUsage ??= getSidebarUsage(absence)
      rows.set(absence.user.id, row)
    })

    const groupOrder = new Map(
      leaderGroups.myLeaderGroups.map((group, index) => [group.id, index])
    )

    const sortedRows = [...rows.values()]
      .map(row => ({
        ...row,
        features: [...row.features].sort(
          (a, b) => a.startAt.getTime() - b.startAt.getTime()
        ),
      }))
      .sort((a, b) => {
        const aOrder =
          groupOrder.get(a.leaderGroupId) ?? Number.MAX_SAFE_INTEGER
        const bOrder =
          groupOrder.get(b.leaderGroupId) ?? Number.MAX_SAFE_INTEGER

        if (aOrder !== bOrder) return aOrder - bOrder
        return a.userName.localeCompare(b.userName)
      })

    return sortedRows.reduce<
      {
        leaderGroupId: string
        leaderGroupName: string
        rows: UserRow[]
      }[]
    >((acc, row) => {
      const currentGroup = acc[acc.length - 1]

      if (
        !currentGroup ||
        currentGroup.leaderGroupId !== row.leaderGroupId ||
        currentGroup.leaderGroupName !== row.leaderGroupName
      ) {
        acc.push({
          leaderGroupId: row.leaderGroupId,
          leaderGroupName: row.leaderGroupName,
          rows: [row],
        })
        return acc
      }

      currentGroup.rows.push(row)
      return acc
    }, [])
  }, [
    absences,
    data?.allUsersList,
    leaderGroup,
    leaderGroups.myLeaderGroups,
    translations.none,
    users,
  ])

  const datetimeRange = useMemo(() => {
    if (absences.length === 0) {
      return {
        datetimeStart: startOfDay(from ?? new Date()),
        datetimeEnd: endOfDay(to ?? from ?? new Date()),
      }
    }

    const absenceStarts = absences.map(absence => parseNormalizedStart(absence))
    const absenceEnds = absences.map(absence => parseNormalizedEnd(absence))
    const minAbsenceStart = new Date(
      Math.min(...absenceStarts.map(date => date.getTime()))
    )
    const maxAbsenceEnd = new Date(
      Math.max(...absenceEnds.map(date => date.getTime()))
    )

    return {
      datetimeStart: startOfDay(
        byCreatedDate || !from
          ? minAbsenceStart
          : new Date(Math.min(minAbsenceStart.getTime(), from.getTime()))
      ),
      datetimeEnd: endOfDay(
        byCreatedDate || !to
          ? maxAbsenceEnd
          : new Date(Math.max(maxAbsenceEnd.getTime(), to.getTime()))
      ),
    }
  }, [absences, byCreatedDate, from, to])

  const dayColumnWidth =
    (zoom / GANTT_COLUMN_SCALE_DIVISOR) * GANTT_BASE_COLUMN_WIDTH
  const headerMode = dayColumnWidth < WEEKLY_HEADER_THRESHOLD ? 'weeks' : 'days'
  const timelineRowsHeight = groupedRows.reduce(
    (height, group, index) =>
      height +
      GANTT_DESKTOP_ROW_HEIGHT +
      group.rows.length * GANTT_DESKTOP_ROW_HEIGHT +
      (index > 0 ? GANTT_GROUP_GAP : 0),
    0
  )
  const timelineMinHeight = GANTT_DESKTOP_HEADER_HEIGHT + timelineRowsHeight

  if (isMobile) return null

  if (groupedRows.length === 0) {
    return (
      <div className="flex justify-center py-10 text-sm text-muted-foreground">
        {translations.noData}
      </div>
    )
  }

  return (
    <div
      className="relative pb-4"
      style={{ width: isMobile ? '100%' : `${1600}px`, maxWidth: '100%' }}
    >
      <AbsenceDialog
        ref={dialogRef}
        canAddForOthers={canEditForOthers}
        createLockedBefore={createLockedBefore}
        hideTrigger
      />

      {!isMobile && (
        <div className="absolute right-2 top-2 z-[2] flex items-center gap-2 rounded-md bg-background/90 p-1 shadow-sm">
          <button
            type="button"
            className="cursor-pointer rounded bg-gray-200 px-2 py-1 hover:bg-gray-300 disabled:opacity-50"
            onClick={() => setZoom(value => Math.max(MIN_ZOOM, value - 5))}
            disabled={zoom <= MIN_ZOOM}
          >
            -
          </button>
          <span className="text-sm">{zoom}%</span>
          <button
            type="button"
            className="cursor-pointer rounded bg-gray-200 px-2 py-1 hover:bg-gray-300 disabled:opacity-50"
            onClick={() => setZoom(value => Math.min(MAX_ZOOM, value + 5))}
            disabled={zoom >= MAX_ZOOM}
          >
            +
          </button>
        </div>
      )}

      <GanttProvider
        className="border"
        range="daily"
        zoom={zoom}
        columnDurationMinutes={60 * 24}
        datetimeStart={datetimeRange.datetimeStart}
        datetimeEnd={datetimeRange.datetimeEnd}
      >
        <GanttSidebar compact={isMobile} hideDuration={hideSidebarSums}>
          {groupedRows.map(group => (
            <GanttSidebarGroup
              key={group.leaderGroupId || group.leaderGroupName}
              name={group.leaderGroupName}
            >
              {group.rows.map(row => (
                <GanttSidebarItem
                  key={row.userId}
                  trailingContent={
                    !hideSidebarSums && row.sidebarUsage ? (
                      <span className="text-xs">{row.sidebarUsage}</span>
                    ) : undefined
                  }
                  feature={{
                    id: row.userId,
                    name: row.userName,
                    startAt:
                      row.features[0]?.startAt ?? datetimeRange.datetimeStart,
                    endAt:
                      row.features[row.features.length - 1]?.endAt ??
                      datetimeRange.datetimeEnd,
                    duration: row.features.reduce(
                      (sum, feature) =>
                        sum +
                        (feature.endAt.getTime() - feature.startAt.getTime()) /
                          60000,
                      0
                    ),
                    hideDuration: hideSidebarSums,
                  }}
                />
              ))}
            </GanttSidebarGroup>
          ))}
        </GanttSidebar>

        <GanttTimeline
          className="min-h-full"
          style={{ minHeight: timelineMinHeight }}
        >
          <AbsenceGanttHeader
            datetimeStart={datetimeRange.datetimeStart}
            datetimeEnd={datetimeRange.datetimeEnd}
            headerMode={headerMode}
            dayColumnWidth={dayColumnWidth}
            contentMinHeight={timelineRowsHeight}
          />

          <GanttFeatureList>
            {groupedRows.map(group => (
              <GanttFeatureListGroup
                key={`timeline-${group.leaderGroupId || group.leaderGroupName}`}
              >
                {group.rows.map(row => (
                  <GanttFeatureRow
                    key={row.userId}
                    className="border-border/50 border-b"
                    features={row.features}
                    handleViewFeature={() => undefined}
                    onFeatureClick={feature => {
                      if (!feature.id) return
                      void navigate(`/absences/${feature.id}`)
                    }}
                    getContextMenuItems={feature => {
                      if (!feature.id) return []

                      const absence = absencesById.get(feature.id)
                      if (!absence) return []

                      return [
                        {
                          id: 'open-absence',
                          label: translations.open,
                          onClick: () => {
                            void navigate(`/absences/${absence.id}`)
                          },
                        },
                        {
                          id: 'edit-absence',
                          label: translations.editAbsence,
                          onClick: () => {
                            dialogRef.current?.open({
                              id: absence.id,
                              user: absence.user.id,
                              absenceType: absence.absenceType?.id ?? '',
                              datetimeStart: new Date(absence.datetimeStart),
                              datetimeEnd: subSeconds(
                                new Date(absence.datetimeEnd),
                                1
                              ),
                              reason: absence.reason ?? '',
                              files: (absence.files ?? []).map(file => ({
                                id: file.id,
                                name: file.fileName,
                                url: file.fileUrl,
                              })),
                            })
                          },
                        },
                      ]
                    }}
                  />
                ))}
              </GanttFeatureListGroup>
            ))}
          </GanttFeatureList>
        </GanttTimeline>
      </GanttProvider>
    </div>
  )
}

function createFeature(absence: AbsenceRequestNode): GanttFeature {
  const startAt = parseNormalizedStart(absence)
  const endAt = parseNormalizedEnd(absence)
  const isDaily = absence.absenceType?.durationMode === 'DAILY'

  return {
    id: absence.id,
    name: absence.absenceType?.name ?? '',
    startAt,
    endAt,
    group: {
      id: absence.absenceType?.id ?? absence.id,
      name: absence.absenceType?.name ?? '',
      color: ABSENCE_STATUS_COLORS[absence.requestStatus],
    },
    tooltipContent: (
      <div>
        <h3 className="font-medium">{absence.user.fullName}</h3>
        <ul>
          <li>{absence.absenceType?.name ?? ''}</li>
          <li>
            {isDaily
              ? `${format(startAt, 'PP')} - ${format(endAt, 'PP')}`
              : `${format(startAt, 'dd.MM.yyyy HH:mm')} - ${format(
                  endAt,
                  'dd.MM.yyyy HH:mm'
                )}`}
          </li>
          {!!absence.reason && <li>{absence.reason}</li>}
        </ul>
      </div>
    ),
  }
}

function parseNormalizedStart(absence: AbsenceRequestNode) {
  return parseISO(absence.datetimeStart)
}

function parseNormalizedEnd(absence: AbsenceRequestNode) {
  const end = parseISO(absence.datetimeEnd)

  if (absence.absenceType?.durationMode === 'DAILY') {
    return subSeconds(end, 1)
  }

  return end
}

function getSidebarUsage(absence: AbsenceRequestNode) {
  const report = absence.periodReport
  if (!report) return null

  if (report.absenceType.durationMode === 'DAILY') {
    if (report.hoursMax !== null) {
      return `${Math.ceil(report.hoursUsed / 24)}/${Math.ceil(
        report.hoursMax / 24
      )} d`
    }

    if (report.absencesMax !== null) {
      return `${report.absencesTotal}/${report.absencesMax} d`
    }

    return null
  }

  if (report.hoursMax !== null) {
    return `${report.hoursUsed}/${report.hoursMax} h`
  }

  return null
}

function AbsenceGanttHeader({
  datetimeStart,
  datetimeEnd,
  headerMode,
  dayColumnWidth,
  contentMinHeight,
}: {
  datetimeStart: Date
  datetimeEnd: Date
  headerMode: 'days' | 'weeks'
  dayColumnWidth: number
  contentMinHeight: number
}) {
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfDay(datetimeStart),
        end: startOfDay(datetimeEnd),
      }),
    [datetimeEnd, datetimeStart]
  )

  const months = useMemo(() => {
    const start = startOfMonth(datetimeStart)
    const end = startOfMonth(datetimeEnd)
    const result: { label: string; days: number }[] = []

    for (let current = start; current <= end; current = addMonths(current, 1)) {
      const monthStart =
        current < startOfDay(datetimeStart)
          ? startOfDay(datetimeStart)
          : current
      const monthEnd = endOfMonth(current)
      const clampedMonthEnd =
        monthEnd > startOfDay(datetimeEnd) ? startOfDay(datetimeEnd) : monthEnd

      result.push({
        label: format(current, 'MMMM yyyy'),
        days: differenceInCalendarDays(clampedMonthEnd, monthStart) + 1,
      })
    }

    return result
  }, [datetimeEnd, datetimeStart])

  const weeks = useMemo(() => {
    const rangeStart = startOfDay(datetimeStart)
    const rangeEnd = startOfDay(datetimeEnd)
    const result: { label: string; days: number }[] = []

    let current = startOfWeek(rangeStart, { weekStartsOn: 1 })

    while (current <= rangeEnd) {
      const weekStart = current < rangeStart ? rangeStart : current
      const rawWeekEnd = endOfWeek(current, { weekStartsOn: 1 })
      const weekEnd = rawWeekEnd > rangeEnd ? rangeEnd : rawWeekEnd

      result.push({
        label: String(getISOWeek(weekStart)),
        days: differenceInCalendarDays(weekEnd, weekStart) + 1,
      })

      current = new Date(rawWeekEnd.getTime() + 1)
    }

    return result
  }, [datetimeEnd, datetimeStart])

  return (
    <div className="relative flex flex-col">
      <div className="sticky top-0 z-20 bg-backdrop/90">
        <div className="relative flex">
          {months.map(month => (
            <div
              className="shrink-0 border-border/50 border-b px-3 py-2 text-muted-foreground text-xs"
              key={`${month.label}-${month.days}`}
              style={{
                width: `calc(${month.days} * var(--gantt-column-width))`,
              }}
            >
              {month.label}
            </div>
          ))}
        </div>

        <div className="relative flex">
          {headerMode === 'weeks'
            ? weeks.map(week => (
                <div
                  className="shrink-0 border-border/50 border-b px-2 py-1 text-center text-xs text-muted-foreground"
                  key={`${week.label}-${week.days}`}
                  style={{
                    width: `calc(${week.days} * var(--gantt-column-width))`,
                  }}
                >
                  {week.label}
                </div>
              ))
            : days.map(day => (
                <div
                  className="shrink-0 border-border/50 border-b px-2 py-1 text-center text-xs text-muted-foreground"
                  key={day.toISOString()}
                  style={{ width: 'var(--gantt-column-width)' }}
                >
                  {format(day, dayColumnWidth > 28 ? 'EEE d' : 'd')}
                </div>
              ))}
        </div>
      </div>

      <div
        className="relative grid h-full w-full divide-x divide-border/50"
        onMouseLeave={() => setHoveredDate(null)}
        style={{
          gridTemplateColumns: `repeat(${days.length}, var(--gantt-column-width))`,
          minHeight: contentMinHeight,
        }}
      >
        {days.map(day => (
          <div
            className="relative h-full min-h-[1px]"
            key={day.toISOString()}
            onMouseEnter={() => setHoveredDate(day)}
          >
            <div
              className="h-full w-full"
              style={{
                backgroundColor:
                  day.getDay() === 0 || day.getDay() === 6
                    ? 'rgba(15, 23, 42, 0.04)'
                    : undefined,
              }}
            />
          </div>
        ))}

        {hoveredDate && (
          <div
            className="pointer-events-none absolute top-2 z-30 -translate-x-1/2 rounded-md border border-border/50 bg-background/95 px-2 py-1 text-xs text-foreground shadow-sm"
            style={{
              left: `calc(${differenceInCalendarDays(
                startOfDay(hoveredDate),
                startOfDay(datetimeStart)
              )} * var(--gantt-column-width) + var(--gantt-column-width) / 2)`,
            }}
          >
            {format(hoveredDate, 'PPP')}
          </div>
        )}
      </div>
    </div>
  )
}
